import { omit } from 'remeda';
import { type Descendant } from 'slate';

import { Editor } from '@@editor/helpers';
import { ELEMENT_TYPES, type ElementType, NameSource } from '@@editor/helpers/Element';
import {
    createElementFromSingleLineState,
    createElementFromString,
} from '@@editor/serialization/UnitySerializer/deserializeNodes';
import { isFile } from '@@scripts/utils';
import { isAssetPayload } from '@@utils/assets';

import { createEmbedBlock } from './';

type InitialOptions = {
    nodeType: ElementType;
    children?: Descendant[];
};

const createEmbedBlockForFile = (
    file,
    nodeType,
    initialChildren: Descendant[] = [{ text: '' }],
) => {
    let data;
    let children: Descendant[] = initialChildren;

    if (isFile(file)) {
        data = {
            mimetype: file.type,
            src: null,
            originalSrc: file,
            embed: {
                url: null,
                elvisId: null,
                nameSource: NameSource.ASSETS,
            },
        };
    } else if (typeof file === 'string' || isAssetPayload(file)) {
        data = {
            mimetype: null,
            src: null,
            originalSrc: file,
            embed: {
                url: null,
                elvisId: null,
                nameSource: NameSource.ASSETS,
            },
        };
    } else {
        const { caption, credit } = file.embed;

        data = {
            ...file,
            embed: {
                ...omit(file.embed, ['caption', 'credit']),
                nameSource: NameSource.ASSETS,
            },
        };

        children = [
            createElementFromSingleLineState(ELEMENT_TYPES.EMBED_CAPTION)(caption),
            createElementFromString(ELEMENT_TYPES.EMBED_CREDIT)(credit),
        ];
    }

    const block = createEmbedBlock(nodeType, data, children);

    return block;
};

const embedBlock = (editor: Editor, file, initialOptions: InitialOptions, options) => {
    const children = options.children ?? initialOptions.children;
    const block = createEmbedBlockForFile(file, initialOptions.nodeType, children);

    if (!block) {
        return;
    }

    Editor.insertElement(editor, block, options);
};

const embedFiles = (editor: Editor, data, initialOptions: InitialOptions, options) => {
    if (Array.isArray(data)) {
        return Promise.all(data.map((item) => embedBlock(editor, item, initialOptions, options)));
    }

    return embedBlock(editor, data, initialOptions, options);
};

export const createGenerateEmbedFiles =
    (initialOptions: InitialOptions) =>
    (editor, data, options = {}) =>
        embedFiles(editor, data, initialOptions, options);
